import {
  CheckOutlined,
  CloseOutlined,
  InfoCircleOutlined
} from '@ant-design/icons'
import type { UserRole } from '@lmc/ts-common'
import { Icon, IconType } from '@lmc/ui-kit'
import { NAMESPACE } from 'locale'
import { reportingConstants } from 'locale/reporting'
import { reportsConstants } from 'locale/team'
import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import ExternalLinkIcon from './ExternalLinkIcon'
import { ROLE_PREVIEW_COPY } from './roleInfoCopy'

export type RoleInfoCardProps = {
  // Accept `string` (not just `UserRole`) so wizard-only roles such as
  // 'it-security' (CM-2888 pending) render through the same card.
  role: UserRole | string
  collapsible?: boolean
  defaultExpanded?: boolean
  testId?: string
}

// TODO(CM-3425): replace with the support article URL once design provides it.
// Until then the link renders inert (no href, click does nothing) so it doesn't
// open a useless blank tab pointing at the current page.
const OVERVIEW_LINK_URL: string | null = null

const Card = styled.div<{ $expanded: boolean }>`
  background: #f0fcff;
  border: 1px solid #8fcafd;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: ${({ $expanded }) => ($expanded ? '8px' : '0')};
  width: 100%;
`

const Heading = styled.div<{ $clickable: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  font-size: 18px;
  line-height: 24px;
  letter-spacing: 0.4px;
  color: ${({ theme }) => theme.color.neutral.color3};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
`

const SectionTitle = styled.p`
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: ${({ theme }) => theme.color.neutral.color3};
  margin: 4px 0 0;
`

const SectionItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;
`

const Item = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  color: ${({ theme }) => theme.color.neutral.color3};
  font-size: 16px;
  line-height: 24px;
`

const TickGlyph = styled(CheckOutlined)`
  color: ${({ theme }) => theme.color.status.success};
  font-size: 16px;
  flex-shrink: 0;
  margin-top: 4px;
`

const CrossGlyph = styled(CloseOutlined)`
  color: ${({ theme }) => theme.color.status.danger};
  font-size: 16px;
  flex-shrink: 0;
  margin-top: 4px;
`

const InlineSettingsIcon = styled(Icon)`
  display: inline-flex;
  vertical-align: middle;
  margin: 0 2px;
`

const ExtraNote = styled.p`
  margin: 8px 0 0;
  font-size: 16px;
  line-height: 24px;
  color: ${({ theme }) => theme.color.neutral.color3};
`

const KeyworkerLink = styled.a`
  color: ${({ theme }) => theme.color.status.primary};
  text-decoration: underline;
  display: inline-flex;
  align-items: center;
  gap: 4px;
`

const OverviewLink = styled.a`
  margin-top: 8px;
  color: ${({ theme }) => theme.color.status.primary};
  text-decoration: underline;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 16px;
  line-height: 24px;
`

export const RoleInfoCard = ({
  role,
  collapsible = false,
  defaultExpanded = true,
  testId
}: RoleInfoCardProps) => {
  const [expanded, setExpanded] = useState(defaultExpanded)
  const { t } = useTranslation(NAMESPACE.TEAM)
  const { t: tReporting } = useTranslation(NAMESPACE.REPORTING)
  const nominative = useSelector(
    (s: { global: { home: { preferredPeopleNominative?: string } } }) =>
      s.global.home?.preferredPeopleNominative ?? 'service user'
  )

  const copy = ROLE_PREVIEW_COPY[role]
  if (!copy) return null

  // Roles missing from reportingConstants (e.g. 'it-security' pending
  // CM-2888) supply their own `roleNameKey` in the copy table.
  const roleName = copy.roleNameKey
    ? t(copy.roleNameKey)
    : tReporting(reportingConstants[role as UserRole])
  const heading = t(reportsConstants.ROLE_PREVIEW_HEADING_PREFIX, {
    role: roleName
  })

  const toggle = () => setExpanded((v) => !v)

  return (
    <Card $expanded={expanded} data-testid={testId}>
      <Heading
        $clickable={collapsible}
        onClick={collapsible ? toggle : undefined}
        onKeyDown={
          collapsible
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  toggle()
                }
              }
            : undefined
        }
        role={collapsible ? 'button' : undefined}
        tabIndex={collapsible ? 0 : undefined}
        aria-expanded={collapsible ? expanded : undefined}
      >
        <InfoCircleOutlined style={{ color: '#1890ff' }} />
        {heading}
      </Heading>
      {expanded && (
        <>
          {copy.sections.map((section) => (
            <div key={section.heading}>
              <SectionTitle>
                {section.heading === 'careOffice'
                  ? t(reportsConstants.ROLE_PREVIEW_CARE_OFFICE)
                  : t(reportsConstants.ROLE_PREVIEW_CARER_APP)}
              </SectionTitle>
              <SectionItems>
                {section.items.map((item, idx) => (
                  <Item key={`${section.heading}-${idx}`}>
                    {item.variant === 'tick' ? (
                      <TickGlyph aria-hidden />
                    ) : (
                      <CrossGlyph aria-hidden />
                    )}
                    <span>
                      <Trans
                        i18nKey={item.copyKey}
                        ns={NAMESPACE.TEAM}
                        values={{ serviceUser: nominative }}
                        components={{
                          settingsIcon: (
                            <InlineSettingsIcon
                              customIcon={IconType.Settings}
                              size={16}
                            />
                          )
                        }}
                      />
                    </span>
                  </Item>
                ))}
              </SectionItems>
            </div>
          ))}
          {copy.extraNote && (
            <ExtraNote>
              <Trans
                i18nKey={copy.extraNote.copyKey}
                ns={NAMESPACE.TEAM}
                components={{
                  bold: <strong />,
                  link: (
                    <KeyworkerLink
                      href={copy.extraNote.linkHref}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <ExternalLinkIcon />
                    </KeyworkerLink>
                  )
                }}
              />
            </ExtraNote>
          )}
          <OverviewLink
            href={OVERVIEW_LINK_URL ?? undefined}
            target={OVERVIEW_LINK_URL ? '_blank' : undefined}
            rel={OVERVIEW_LINK_URL ? 'noreferrer' : undefined}
            onClick={OVERVIEW_LINK_URL ? undefined : (e) => e.preventDefault()}
            aria-disabled={OVERVIEW_LINK_URL ? undefined : 'true'}
          >
            {t(reportsConstants.ROLE_PREVIEW_OVERVIEW_LINK)}
            <ExternalLinkIcon />
          </OverviewLink>
        </>
      )}
    </Card>
  )
}
